import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import FAQ from '@/data/faq';
import './FaqPage.css';

/**
 * FAQ.
 *
 * Content is generated from .claude/docs/FAQ-DRAFT.md by tools/faq-build.mjs, so
 * the markdown stays the single source of truth rather than the same prose
 * drifting between two files.
 *
 * Makes no network calls, so it costs nothing to visit and never wakes the
 * scale-to-zero backend.
 */

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

/** Minimal inline renderer: bold and paragraph breaks are all the source uses. */
function Answer({ text }) {
  return (
    <>
      {text.split(/\n{2,}/).map((para, i) => (
        <p key={i}>
          {para.split(/(\*\*[^*]+\*\*)/g).map((chunk, j) =>
            chunk.startsWith('**') && chunk.endsWith('**')
              ? <strong key={j}>{chunk.slice(2, -2)}</strong>
              : <span key={j}>{chunk.replace(/\n/g, ' ')}</span>
          )}
        </p>
      ))}
    </>
  );
}

export default function FaqPage() {
  const [query, setQuery] = useState('');

  useEffect(() => { document.title = 'FAQ — Stars at Birth'; }, []);

  const sections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FAQ;
    return FAQ
      .map((s) => ({
        ...s,
        items: s.items.filter(
          (it) => it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q)
        ),
      }))
      .filter((s) => s.items.length);
  }, [query]);

  const total = sections.reduce((n, s) => n + s.items.length, 0);

  return (
    <div className="faq">
      <header className="faq-head">
        <Link className="faq-back" to="/">&larr; Stars at Birth</Link>
        <h1 className="faq-title">Questions</h1>
        <p className="faq-sub">
          What this measures, how it differs from astrology, and what you actually get.
        </p>

        <input
          className="faq-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search the questions"
          aria-label="Search the questions"
        />
        {query && (
          <p className="faq-count">
            {total} {total === 1 ? 'question' : 'questions'} matching &ldquo;{query}&rdquo;
          </p>
        )}
      </header>

      {!query && (
        <nav className="faq-toc" aria-label="Sections">
          {FAQ.map((s) => (
            <a key={s.title} href={`#${slug(s.title)}`}>{s.title}</a>
          ))}
        </nav>
      )}

      {sections.map((s) => (
        <section className="faq-section" id={slug(s.title)} key={s.title}>
          <h2 className="faq-h2">{s.title}</h2>
          {s.items.map((it) => (
            <details className="faq-item" key={it.q}>
              <summary>{it.q}</summary>
              <div className="faq-answer"><Answer text={it.a} /></div>
            </details>
          ))}
        </section>
      ))}

      {total === 0 && (
        <p className="faq-empty">
          Nothing matches that. Try a shorter word — &ldquo;zenith&rdquo;, &ldquo;print&rdquo;, or
          &ldquo;astrology&rdquo;.
        </p>
      )}

      <footer className="faq-foot">
        <Link className="faq-cta" to="/app">Find your band</Link>
        <p>
          Measurements from Gaia DR3 · ESA/Gaia/DPAC · names from the AT-HYG registry ·
          constellation boundaries after Delporte (1930)
        </p>
      </footer>
    </div>
  );
}
